import {useState} from 'react';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Input
} from '@kroll-resolver-crisp/ui-components-react';
import {type Item} from '@/api/items';
import {type FlagColor} from '@/api/flags';
import {ItemsCollection} from '@/components/items/ItemsCollection';
import {ItemsViewToggle} from '@/components/items/ItemsToolbar';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';

type ItemsGridProps = {
  readonly items: Item[];
  readonly showSearch?: boolean;
  readonly onSelect?: (item: Item) => void;
  readonly selectedIds?: Set<string>;
  // The collection/job's search query, if known - when present, each card's thumbnail shows which
  // of its terms are literally present in that item's own text.
  readonly searchTerm?: string;
  // When set, every card and row carries the analyst's own flag picker - see useSetItemFlag.
  readonly onSetFlag?: (item: Item, color: FlagColor | null) => void;
  // Puts the blur toggle beside the layout toggle, for a host with no page header of its own to
  // carry it (the Collections run-items modal). A page passes nothing and keeps it in its header.
  readonly showBlurToggle?: boolean;
};

// A self-contained view of a list of items (run and job detail): the shared grid/list, its own
// quick filter and layout toggle. Pages with a full toolbar use ItemsCollection directly.
export function ItemsGrid({
  items,
  showSearch,
  onSelect,
  selectedIds,
  searchTerm,
  onSetFlag,
  showBlurToggle
}: ItemsGridProps) {
  const [query, setQuery] = useState('');

  const needle = query.toLowerCase();
  const filtered = query
    ? items.filter(
        (i) =>
          (i.title?.toLowerCase().includes(needle) ?? false) ||
          (i.author_handle?.toLowerCase().includes(needle) ?? false) ||
          i.external_id.toLowerCase().includes(needle)
      )
    : items;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {showSearch ? (
          <Input
            placeholder="Filter by title, handle, ID…"
            value={query}
            className="max-w-sm"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {showBlurToggle ? <SensitiveMediaToggle /> : null}
          <ItemsViewToggle />
        </div>
      </div>

      {filtered.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No items</EmptyTitle>
            <EmptyDescription>
              {query ? 'Nothing matches this filter.' : 'Nothing has been collected here yet.'}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ItemsCollection
          items={filtered}
          {...(selectedIds ? {selectedItemIds: selectedIds} : {})}
          {...(onSelect ? {onOpen: onSelect} : {})}
          {...(onSetFlag ? {onSetFlag} : {})}
          {...(searchTerm ? {searchTerm} : {})}
        />
      )}
    </div>
  );
}
